'use client';

import { useState, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { IconUpload, IconPlayerStop } from '@tabler/icons-react';

import { cn } from '@/lib/utils';
import { formatDuration } from '@/lib/format-duration';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

import {
  photoScanningQueries,
  useScanFromCameraMutation
} from '@/features/photo-scanning';

import {
  DEFAULT_SCAN_DELAY_MS,
  MAX_SCAN_DELAY_MS,
  MIN_SCAN_DELAY_MS
} from '../constants';
import type { ScanRunResult } from '../types';

export function ScannerSimulator() {
  const t = useTranslations('devTools.scanner');
  const [batchId, setBatchId] = useState<number | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [delayMs, setDelayMs] = useState<number>(DEFAULT_SCAN_DELAY_MS);
  const [startSeq, setStartSeq] = useState<string>('');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [log, setLog] = useState<ScanRunResult[]>([]);
  const stopRef = useRef(false);

  const { data: batchesData, isLoading } = useQuery(
    photoScanningQueries.list({
      per_page: 30,
      sort_by: 'created_at',
      sort_direction: 'desc'
    })
  );
  const batches = batchesData?.data ?? [];

  const scanMutation = useScanFromCameraMutation();

  const handleStart = async () => {
    if (!batchId) {
      toast.error(t('noBatchToast'));
      return;
    }
    if (files.length === 0) {
      toast.error(t('noFilesToast'));
      return;
    }

    setRunning(true);
    setProgress(0);
    setLog([]);
    stopRef.current = false;

    const startSeqNum = startSeq ? Number(startSeq) : undefined;
    const results: ScanRunResult[] = [];

    for (let i = 0; i < files.length; i++) {
      if (stopRef.current) break;
      const file = files[i];
      const t0 = performance.now();
      try {
        const expected =
          startSeqNum !== undefined ? startSeqNum + i : undefined;
        const response = await scanMutation.mutateAsync({
          batchId,
          image: file,
          expectedSequence: expected
        });
        results.push({
          fileName: file.name,
          ok: true,
          message: 'OK',
          pageNumber: response?.data?.page_number,
          durationMs: Math.round(performance.now() - t0)
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        results.push({
          fileName: file.name,
          ok: false,
          message,
          durationMs: Math.round(performance.now() - t0)
        });
      }
      setLog([...results]);
      setProgress(Math.round(((i + 1) / files.length) * 100));
      if (delayMs > 0 && i < files.length - 1) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }

    setRunning(false);

    const okCount = results.filter((r) => r.ok).length;
    const batchLabel =
      batches.find((b) => b.id === batchId)?.batch_number ?? `#${batchId}`;
    if (okCount === results.length) {
      toast.success(t('successToast', { count: okCount, batch: batchLabel }));
    } else {
      toast.warning(
        t('partialFailToast', { ok: okCount, total: results.length })
      );
    }
  };

  const handleStop = () => {
    stopRef.current = true;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent className='flex flex-col gap-6'>
        <div className='grid gap-4 md:grid-cols-2'>
          <div className='space-y-2'>
            <Label htmlFor='dev-tools-batch'>{t('selectBatchLabel')}</Label>
            <Select
              value={batchId ? String(batchId) : ''}
              onValueChange={(value) => setBatchId(Number(value))}
              disabled={isLoading}
            >
              <SelectTrigger id='dev-tools-batch'>
                <SelectValue placeholder={t('selectBatchPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {batches.length === 0 ? (
                  <div className='text-muted-foreground px-3 py-2 text-sm'>
                    {t('selectBatchEmpty')}
                  </div>
                ) : (
                  batches.map((b) => (
                    <SelectItem key={b.id} value={String(b.id)}>
                      {b.batch_number}
                      {b.container_id ? ` · ${b.container_id}` : ''}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='dev-tools-delay'>{t('delayLabel')}</Label>
            <Input
              id='dev-tools-delay'
              type='number'
              min={MIN_SCAN_DELAY_MS}
              max={MAX_SCAN_DELAY_MS}
              value={delayMs}
              onChange={(e) => setDelayMs(Number(e.target.value))}
            />
            <p className='text-muted-foreground text-xs'>{t('delayHint')}</p>
          </div>

          <div className='space-y-2 md:col-span-2'>
            <Label htmlFor='dev-tools-files'>{t('filesLabel')}</Label>
            <Input
              id='dev-tools-files'
              type='file'
              accept='image/jpeg,image/png'
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            />
            <p className='text-muted-foreground text-xs'>{t('filesHint')}</p>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='dev-tools-seq'>{t('expectedSequenceLabel')}</Label>
            <Input
              id='dev-tools-seq'
              type='number'
              min={1}
              value={startSeq}
              onChange={(e) => setStartSeq(e.target.value)}
            />
            <p className='text-muted-foreground text-xs'>
              {t('expectedSequenceHint')}
            </p>
          </div>
        </div>

        <div className='flex items-center gap-3'>
          <Button onClick={handleStart} disabled={running}>
            <IconUpload className='size-4' />
            {t('uploadButton')}
          </Button>
          {running && (
            <Button variant='outline' onClick={handleStop}>
              <IconPlayerStop className='size-4' />
              {t('stopButton')}
            </Button>
          )}
          <div className='flex-1'>
            <div className='mb-1 flex justify-between text-xs'>
              <span className='text-muted-foreground'>
                {t('progressLabel')}
              </span>
              <span className='text-muted-foreground'>{progress}%</span>
            </div>
            <Progress value={progress} />
          </div>
        </div>

        <div className='space-y-2'>
          <h4 className='text-sm font-medium'>{t('logHeader')}</h4>
          {log.length === 0 ? (
            <p className='text-muted-foreground text-sm'>{t('logEmpty')}</p>
          ) : (
            <div className='rounded-md border'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('logColumns.file')}</TableHead>
                    <TableHead>{t('logColumns.result')}</TableHead>
                    <TableHead>{t('logColumns.page')}</TableHead>
                    <TableHead>{t('logColumns.duration')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {log.map((entry, i) => (
                    <TableRow key={`${entry.fileName}-${i}`}>
                      <TableCell className='max-w-xs truncate font-mono text-xs'>
                        {entry.fileName}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={entry.ok ? 'default' : 'destructive'}
                          className={cn(
                            entry.ok && 'bg-emerald-500/15 text-emerald-700'
                          )}
                        >
                          {entry.ok ? t('resultOk') : t('resultFail')}
                        </Badge>
                      </TableCell>
                      <TableCell className='font-mono text-xs'>
                        {entry.pageNumber ?? '—'}
                      </TableCell>
                      <TableCell className='font-mono text-xs'>
                        {formatDuration(entry.durationMs)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
